import { getCheckinFormConfig } from '@jq4race/api-client';
import type { CheckinFormConfigResponse } from '@jq4race/shared';
import { useQuery } from '@tanstack/react-query';

interface UseCheckinConfigResult {
  config: CheckinFormConfigResponse | null;
  loading: boolean;
  error: Error | null;
}

// Query key — also the persisted-query allowlist prefix (task 3.5,
// apps/pwa/src/app/query-client.ts) so this response survives a reload
// while offline, per spec "Cached reads for offline operation".
export function checkinConfigKey(locationId: string) {
  return ['checkin-config', locationId] as const;
}

/**
 * useCheckinConfig — converted from a manual useEffect/useState fetch to
 * TanStack Query (task 3.5) so the response is (a) cached per location like
 * every other query in the app and (b) persistable to IndexedDB via the
 * allowlisted persister for offline reads. Return shape kept identical to
 * the pre-slice-3 hook so CheckinPage needed no changes.
 */
export function useCheckinConfig(locationId: string | null): UseCheckinConfigResult {
  const query = useQuery({
    queryKey: checkinConfigKey(locationId ?? ''),
    queryFn: () => getCheckinFormConfig(locationId ?? ''),
    enabled: locationId !== null,
    // Config is stable per session — mirrors useCompsConfig's staleTime for
    // the same underlying endpoint.
    staleTime: 5 * 60 * 1000,
    // Allowlisted for offline persistence (task 3.5, app/query-client.ts) —
    // spec "Cached reads for offline operation" requires checkin config to
    // render from cache while offline.
    networkMode: 'offlineFirst',
    // Adversarial-review fix (Slice 3, finding #3): the pre-conversion
    // manual fetch NEVER retried and NEVER refetched on window focus —
    // useQuery's defaults (retry:3, refetchOnWindowFocus:true) silently
    // changed that. retry:false surfaces a failure promptly (this config
    // gates which fields even render on CheckinPage, so a valet should not
    // wait through 3 exponential-backoff attempts before seeing an error).
    // refetchOnWindowFocus:false prevents the field layout from changing
    // mid-check-in just because the operator tabbed away and back.
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    config: query.data ?? null,
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
  };
}
